import { html, nothing, type TemplateResult } from "lit";
// The Blood Cancer United wordmark, cropped out of the Figma 421:102 lockup:
// the "Dare to Dream Project" line and its star were dropped on request, so only
// the title remains. 1024 x 84, lossless WebP.
import bcuLogoUrl from "./blood-cancer-united.webp";

import "./donate.scss";

const PROCEEDS = "All proceeds go directly to Blood Cancer United's Dare to Dream Project";

const DISCLAIMER =
  "$5 entry up to $500. $5 is one entry. Grand prize is 2 NASCAR tickets + hotel and travel " +
  "to the race in Charlotte, North Carolina, Sunday, October 11, 2026.";

/**
 * Abbreviated sweepstakes rules, supplied by legal on 2026-08-13 and rendered
 * above the disclaimer.
 *
 * Treat this as fixed copy: the dates, the entry cap, the sponsor and the
 * GoFundMe line are the legally required abbreviated form, so it is not ours to
 * trim, reword or reflow for balance. Two paragraphs because the GoFundMe
 * disclaimer has to stand on its own.
 *
 * The only addition is the link on "Official Rules" — the wording is untouched.
 *
 * IT POINTS AT `/officialrules`, WITHOUT THE TRAILING SLASH, and that is a
 * deployment requirement, chosen deliberately on 2026-08-17. The page is built as
 * `officialrules/index.html`, so the host has to resolve the extensionless path to
 * that directory index. Express, Netlify, Vercel and Apache all answer a 301 to
 * `/officialrules/` on their own — verified locally, including against a static
 * server with no rewrite rules. S3 served directly and nginx without `try_files` do
 * NOT, and there this link 404s while `/officialrules/` still works. If that is the
 * host, fix it in the host config rather than by adding the slash here.
 */
const ABBREVIATED_RULES = [
  html`<strong>NO DONATION OR PAYMENT NECESSARY.</strong> Enter the Sweepstakes between 8/24/26 at
    7:00:01 a.m. ET and 9/27/26 at 11:59:59 p.m. ET. Open to legal residents of the 50 U.S. & D.C.,
    at least 18 years of age (or age of majority, whichever is older).
    <strong>GRAND PRIZE WINNER MUST TRAVEL ON OR ABOUT OCTOBER 10-12, 2026.</strong> Limit (100)
    entries per person. Winners selected at random. See
    <a
      class="valvoline-donate__rules-link"
      href="/officialrules"
      target="_blank"
      rel="noopener noreferrer"
      >Official Rules</a
    >
    for complete details including how to enter (including free method of entry), eligibility
    restrictions, odds of winning and limitations. Void where prohibited. Sponsor: VGP Holdings LLC,
    Lexington, KY. Charitable Beneficiary: Blood Cancer United.`,
  html`GoFundMe, Inc. is not responsible for sponsoring or administering this Sweepstakes or
  selecting the winners.`,
];

/**
 * Shown only if the Probability script fails to load — a blocking CSP on the
 * strict CSP, an outage, an offline visitor. Better than a blank card.
 */
const FORM_UNAVAILABLE =
  "The donation form could not be loaded. Please refresh the page or try again shortly.";

export interface DonateProps {
  /** Overrides the bundled Blood Cancer United wordmark. */
  readonly bcuLogo: string;
  /** Probability program the form submits to. */
  readonly programId: string;
  /** Probability environment — `prod` or `stage`. */
  readonly env: string;
  /** True once `loadScript` has rejected; swaps in a notice above the form. */
  readonly formFailed: boolean;
  /** GoFundMe campaign the hidden checkout trigger points at. */
  readonly gofundmeCampaign: string;
}

/**
 * Donation + entry section. Figma desktop 421:351 (1094x1254 card at x317 y3181);
 * mobile 428:908 (402x1372 at y4334).
 *
 * The card's copy is ours. What sits inside it belongs to two other companies:
 *
 * - Probability's `<universal-form-webcomponent>` owns the fields, the validation
 *   and the sweepstakes entry. Its field list comes from the program config on
 *   their server, and the script that upgrades the tag is fetched at runtime
 *   (`lib/load-script.ts`).
 * - GoFundMe's embedded checkout takes the money. It has no imperative API, so
 *   the hidden anchor below is its trigger — see `lib/donate-flow.ts`, which
 *   chains the two: submit validates, the checkout opens, and the entry is only
 *   posted to Probability once the donation completes.
 *
 * Consequences worth knowing before changing this section:
 *
 * - We never see the field values, and there is no `valvoline-donate:submit`
 *   event. Fields, validation, receipts and entry confirmation belong to
 *   Probability — route those requests to their program config, not to here.
 * - Both vendors render into the light DOM with unprefixed classes
 *   (`.form-universal`, `.contain-information`) and bring their own stylesheets.
 *   Our root reset is scoped to skip Probability's subtree — see
 *   driving-sweepstakes.scss — and GoFundMe's modal is its own shadow DOM, so it
 *   cannot be themed to the brand at all.
 */
export function renderDonate({
  bcuLogo,
  programId,
  env,
  formFailed,
  gofundmeCampaign,
}: DonateProps): TemplateResult {
  return html`
    <section class="valvoline-donate" id="donate">
      <div class="valvoline-donate__inner">
        <h2 class="valvoline-donate__heading hide"></h2>

        <div class="valvoline-donate__card">
          <h3 class="valvoline-donate__lede">
            Driving for a dream.
            <span class="valvoline-donate__mark">Donate now.</span>
          </h3>

          <p class="valvoline-donate__proceeds">${PROCEEDS}</p>

          <img
            class="valvoline-donate__logo"
            src=${bcuLogo || bcuLogoUrl}
            alt="Blood Cancer United"
          />

          <div class="valvoline-donate__form">
            ${formFailed
              ? html`<p class="valvoline-donate__form-error" role="alert">${FORM_UNAVAILABLE}</p>`
              : nothing}
            <!--
              Probability's element. It upgrades itself whenever their script
              finishes loading, so it is safe to render before that happens — do
              not gate it behind a ready flag.
            -->
            <universal-form-webcomponent
              program_id=${programId}
              env=${env}
              case="submit"
            ></universal-form-webcomponent>

            <!--
              The GoFundMe checkout trigger. Their SDK has no imperative "open"
              API: it scans the page for anchors whose href carries a campaign
              query parameter and hijacks the click. So the trigger has to be a
              real anchor, present before their script runs, which
              lib/donate-flow.ts clicks once Probability's form has validated.

              Hidden rather than absent because the visible submit belongs to
              Probability — this is plumbing, not a second button. Kept out of
              the tab order and off the accessibility tree for the same reason.
            -->
            <a
              class="valvoline-donate__checkout"
              href="?campaign=${gofundmeCampaign}"
              tabindex="-1"
              aria-hidden="true"
              >Donate</a
            >
          </div>

          ${ABBREVIATED_RULES.map((rule) => html`<p class="valvoline-donate__rules">${rule}</p>`)}

          <p class="valvoline-donate__disclaimer">${DISCLAIMER}</p>
        </div>
      </div>
    </section>
  `;
}
